import React, { useState } from 'react';
import constants from '../../../common/constants';
import ModalDialogButtonV3 from '../../common/ModalDialogButtonV3';
import SubmitForReviewControl from './SubmitForReviewControl';
import { SpaceBetween } from '@amzn/awsui-components-core';

interface Props {
  isContentUnchanged: boolean;
  isValid: boolean;
  onDiscardChanges: () => Promise<any>;
  onSaveChanges: (unlock: boolean) => Promise<any>;
  onBeforeSaving?: () => Promise<boolean>;
  onSubmitForReview: () => Promise<any>;
  showSubmitForReviewButton: boolean;
  validateSubmitForReview?: () => Promise<boolean>;
  submitForReviewDisabled?: boolean;
  isInPeerReview: boolean;
}

const EditContentControls: React.FunctionComponent<Props> = (props: Props) => {
  const {
    isContentUnchanged,
    isValid,
    onDiscardChanges,
    onBeforeSaving,
    onSaveChanges,
    showSubmitForReviewButton,
    onSubmitForReview,
    validateSubmitForReview,
    submitForReviewDisabled,
    isInPeerReview,
  } = props;
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const isSaveDisabled = !isValid || isContentUnchanged;

  const cancelModalText = isSaveDisabled
    ? constants.WORKFLOW_EDIT_IN_REVIEW_EXIT_MODAL_TEXT
    : constants.WORKFLOW_EDIT_IN_REVIEW_DISCARD_MODAL_TEXT;
  const cancelButtonText = isSaveDisabled
    ? constants.WORKFLOW_EDIT_IN_REVIEW_EXIT_BUTTON_TEXT
    : constants.WORKFLOW_EDIT_IN_REVIEW_DISCARD_BUTTON_TEXT;

  const discardChanges = async () => {
    if (onDiscardChanges) {
      await onDiscardChanges();
    }
  };

  const saveChanges = async (exit: boolean = true) => {
    setIsSaving(true);
    try {
      if (onBeforeSaving && !(await onBeforeSaving())) {
        window.scrollTo({
          behavior: 'smooth',
          top: 0,
        });
      } else {
        if (onSaveChanges) {
          await onSaveChanges(exit);
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveModalText = isInPeerReview
    ? constants.WORKFLOW_SAVE_PEER_REVIEW_TEXT
    : constants.WORKFLOW_EDIT_SAVE_MODAL_TEXT;
  const saveButtonText = isInPeerReview
    ? constants.WORKFLOW_EDIT_PEER_REVIEW_EXIT_BUTTON_TEXT
    : constants.WORKFLOW_EDIT_SAVE_AND_CLOSE_BUTTON_TEXT;
  const saveButton = (
    <ModalDialogButtonV3
      variant="primary"
      header={constants.WORKFLOW_EDIT_SAVE_MODAL_HEADER}
      text={saveModalText}
      submitButtonText={saveButtonText}
      onSubmit={saveChanges}
      disabled={isSaveDisabled || isSaving}
    />
  );

  const cancelButton = (
    <ModalDialogButtonV3
      variant="normal"
      header={constants.WORKFLOW_EDIT_HEADER}
      text={cancelModalText}
      submitButtonText={cancelButtonText}
      onSubmit={discardChanges}
      disabled={isSaving}
    />
  );

  const sendForReviewControls = (
    <SubmitForReviewControl
      isContentUnchanged={isContentUnchanged}
      onSaveChanges={onSaveChanges}
      onBeforeSaving={onBeforeSaving}
      onSubmitForReview={onSubmitForReview}
      // @ts-expect-error TS2322 - strict migration
      validateSubmitForReview={validateSubmitForReview}
      // @ts-expect-error TS2322 - strict migration
      disabled={submitForReviewDisabled}
    />
  );

  return (
    <SpaceBetween size="s" direction="horizontal">
      {cancelButton}
      {showSubmitForReviewButton ? sendForReviewControls : null}
      {saveButton}
    </SpaceBetween>
  );
};

export default EditContentControls;
